import { useCallback, useEffect, useRef, useState } from 'react';
import { getDefaultFormat } from './formats.js';
import {
  createDeck as apiCreateDeck,
  loadDeck,
  newDeckId,
  updateDeck as apiUpdateDeck,
} from './deck-storage.js';
import type { DeckRecord } from './types.js';

/**
 * Hook for managing a single deck. The deck is persisted on the
 * server now (was localStorage); mutations debounce for 600ms then
 * fire a PUT.
 *
 * `deckId === 'new'` keeps an in-memory draft until the user adds
 * their first card; on first mutation we POST to mint a real id and
 * the caller can navigate to /decks/:gameKey/:realId.
 */

export type UseDeckResult = {
  deck: DeckRecord | null;
  /** True while the initial fetch is in flight (for existing decks). */
  loading: boolean;
  /** True while a save is in flight or pending. */
  saving: boolean;
  /** Last error from a save / load, if any. */
  error: string | null;
  setName: (name: string) => void;
  setQty: (cardId: string, qty: number) => void;
  add: (cardId: string, n?: number) => void;
  remove: (cardId: string) => void;
  qtyOf: (cardId: string) => number;
};

const SAVE_DEBOUNCE_MS = 600;

function emptyDeck(gameKey: string): DeckRecord {
  const now = new Date().toISOString();
  return {
    id: newDeckId(),
    gameKey,
    formatKey: getDefaultFormat(gameKey).key,
    name: 'Untitled deck',
    entries: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function useDeck(gameKey: string, deckId: string): UseDeckResult {
  const [deck, setDeck] = useState<DeckRecord | null>(() =>
    deckId === 'new' ? emptyDeck(gameKey) : null,
  );
  const [loading, setLoading] = useState<boolean>(deckId !== 'new');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Track whether the deck has been persisted to the server. For
  // 'new' decks this stays false until the first mutation triggers
  // a POST; after that it stays true and updates go through PUT.
  const persistedIdRef = useRef<string | null>(null);

  // Tracks the most recent in-flight PUT promise so we don't
  // overwrite later state with stale responses.
  const saveSeqRef = useRef(0);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initial load + re-sync when route params change.
  useEffect(() => {
    let alive = true;
    if (deckId === 'new') {
      setDeck(emptyDeck(gameKey));
      persistedIdRef.current = null;
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    loadDeck(gameKey, deckId)
      .then((loaded) => {
        if (!alive) return;
        if (loaded) {
          setDeck(loaded);
          persistedIdRef.current = loaded.id;
        } else {
          setError(`Unknown deck: ${deckId}`);
        }
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [gameKey, deckId]);

  /**
   * Debounced save. Persists the current deck state. For brand-new
   * (unsaved) decks, the first save POSTs and patches the local
   * record with the server-issued id.
   */
  const scheduleSave = useCallback(
    (current: DeckRecord) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        const seq = ++saveSeqRef.current;
        setSaving(true);
        setError(null);
        try {
          if (persistedIdRef.current) {
            const updated = await apiUpdateDeck(persistedIdRef.current, {
              name: current.name,
              entries: current.entries,
            });
            if (saveSeqRef.current === seq) {
              setDeck(updated);
            }
          } else {
            // First save of a new deck — POST mints the real id.
            const created = await apiCreateDeck({
              gameKey: current.gameKey,
              formatKey: current.formatKey,
              name: current.name,
              entries: current.entries,
            });
            persistedIdRef.current = created.id;
            if (saveSeqRef.current === seq) {
              setDeck(created);
            }
          }
        } catch (e) {
          if (saveSeqRef.current === seq) {
            setError(e instanceof Error ? e.message : String(e));
          }
        } finally {
          if (saveSeqRef.current === seq) setSaving(false);
        }
      }, SAVE_DEBOUNCE_MS);
    },
    [],
  );

  const mutate = useCallback(
    (fn: (d: DeckRecord) => DeckRecord) => {
      setDeck((prev) => {
        if (!prev) return prev;
        const next = { ...fn(prev), updatedAt: new Date().toISOString() };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const setName = useCallback(
    (name: string) => mutate((d) => ({ ...d, name })),
    [mutate],
  );

  const setQty = useCallback(
    (cardId: string, qty: number) =>
      mutate((d) => {
        const entries = d.entries.filter((e) => e.cardId !== cardId);
        if (qty > 0) entries.push({ cardId, qty });
        return { ...d, entries };
      }),
    [mutate],
  );

  const add = useCallback(
    (cardId: string, n: number = 1) =>
      mutate((d) => {
        const existing = d.entries.find((e) => e.cardId === cardId);
        const entries = d.entries.filter((e) => e.cardId !== cardId);
        const qty = (existing?.qty ?? 0) + n;
        if (qty > 0) entries.push({ cardId, qty });
        return { ...d, entries };
      }),
    [mutate],
  );

  const remove = useCallback(
    (cardId: string) =>
      mutate((d) => {
        const existing = d.entries.find((e) => e.cardId === cardId);
        if (!existing) return d;
        const entries = d.entries.filter((e) => e.cardId !== cardId);
        if (existing.qty > 1) entries.push({ cardId, qty: existing.qty - 1 });
        return { ...d, entries };
      }),
    [mutate],
  );

  const qtyOf = useCallback(
    (cardId: string) => deck?.entries.find((e) => e.cardId === cardId)?.qty ?? 0,
    [deck],
  );

  return { deck, loading, saving, error, setName, setQty, add, remove, qtyOf };
}
